/**
 * PageBlocksBuilder — CMS component for building/editing page_blocks.
 *
 * Props:
 *  blocks    {Array}    current block list
 *  onChange  {Function} called with new block array on any change
 */

import { useState } from "react";
import {
  Box, Typography, Button, Paper, IconButton, Tooltip, Grid, Card, CardContent,
  CardActionArea, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel, Divider, Chip,
  Accordion, AccordionSummary, AccordionDetails, Stack,
} from "@mui/material";
import {
  Add, Delete, ArrowUpward, ArrowDownward, Edit, ExpandMore,
  ViewCarousel, Campaign, BarChart, TouchApp, Image, GridView, SpaceBar, Notes,
  YouTube, VideoLibrary, Map, PhotoLibrary, Slideshow, DynamicForm,
} from "@mui/icons-material";
import { useUploadFileMutation } from "../../../app/utilsApiSlice";
import CustomTipTapEditor from "../../../customComponents/CustomTipTapEditor";

// ─── Block type catalogue ─────────────────────────────────────────────────────
const BLOCK_TYPES = [
  {
    type: "hero",
    label: "Hero Section",
    description: "Large full-width banner with title, subtitle, background, and CTA button",
    icon: <ViewCarousel />,
    color: "#1a237e",
    defaults: {
      title: "Welcome to Our School",
      subtitle: "Excellence in Education",
      bg_color: "#1a237e",
      text_color: "#ffffff",
      bg_image: "",
      cta_label: "Learn More",
      cta_url: "/blog",
      min_height: 500,
      overlay_opacity: 0.45,
    },
  },
  {
    type: "banner",
    label: "Announcement Banner",
    description: "Colourful single-line bar for news or calls-to-action",
    icon: <Campaign />,
    color: "#e91e63",
    defaults: {
      text: "Applications for the new school year are now open!",
      bg_color: "#e91e63",
      text_color: "#ffffff",
      cta_label: "Apply Now",
      cta_url: "/blog",
    },
  },
  {
    type: "stats",
    label: "Statistics",
    description: "Display key numbers (students, years, achievements…)",
    icon: <BarChart />,
    color: "#0288d1",
    defaults: {
      title: "Our School in Numbers",
      bg_color: "#f5f7ff",
      text_color: "#1a237e",
      label_color: "#555",
      items: [
        { value: "2,500+", label: "Students" },
        { value: "150+", label: "Teachers" },
        { value: "40+", label: "Years" },
        { value: "98%", label: "Pass rate" },
      ],
    },
  },
  {
    type: "cta",
    label: "Call to Action",
    description: "Centred section that drives visitors to take an action",
    icon: <TouchApp />,
    color: "#1565c0",
    defaults: {
      title: "Ready to Join?",
      text: "Apply now for the upcoming school year. Spaces are limited.",
      bg_color: "#1565c0",
      text_color: "#ffffff",
      cta_label: "Apply Now",
      cta_url: "/blog",
    },
  },
  {
    type: "image_text",
    label: "Image + Text",
    description: "Image on one side, title and text on the other",
    icon: <Image />,
    color: "#2e7d32",
    defaults: {
      title: "About Our School",
      text: "We have been committed to academic excellence for over 40 years. Our dedicated staff and modern facilities create the perfect environment for learning.",
      image: "",
      image_position: "left",
      bg_color: "#ffffff",
      text_color: "#1a1a1a",
      cta_label: "",
      cta_url: "",
    },
  },
  {
    type: "features",
    label: "Feature Cards",
    description: "Grid of cards highlighting key strengths or programmes",
    icon: <GridView />,
    color: "#6a1b9a",
    defaults: {
      title: "Why Choose Us",
      bg_color: "#ffffff",
      text_color: "#1a237e",
      columns: 3,
      items: [
        { emoji: "🎓", title: "Academic Excellence", description: "Top-ranked programmes and qualified teachers." },
        { emoji: "🏫", title: "Modern Facilities", description: "State-of-the-art classrooms and labs." },
        { emoji: "⚽", title: "Sports & Activities", description: "Diverse extracurricular activities for all." },
      ],
    },
  },
  {
    type: "spacer",
    label: "Spacer",
    description: "Add vertical white space between sections",
    icon: <SpaceBar />,
    color: "#757575",
    defaults: { height: 60, bg_color: "transparent" },
  },
  {
    type: "richtext",
    label: "Rich Text",
    description: "Formatted text block with headings, lists, links and images",
    icon: <Notes />,
    color: "#546e7a",
    defaults: { html: "<p>Enter your content here…</p>" },
  },
  {
    type: "youtube",
    label: "YouTube Video",
    description: "Embed a YouTube video by URL or video ID",
    icon: <YouTube />,
    color: "#c62828",
    defaults: {
      url: "",
      title: "",
      aspect_ratio: "16/9",
      bg_color: "#000000",
    },
  },
  {
    type: "video",
    label: "Video Upload",
    description: "Upload and embed a local video file (max 20 MB)",
    icon: <VideoLibrary />,
    color: "#6a1b9a",
    defaults: {
      url: "",
      title: "",
      poster: "",
      bg_color: "#000000",
    },
  },
  {
    type: "map",
    label: "Google Map",
    description: "Embed a Google Maps location via an embed URL",
    icon: <Map />,
    color: "#0f9d58",
    defaults: {
      embed_url: "",
      title: "",
      height: 400,
    },
  },
  {
    type: "google_form",
    label: "Google Form",
    description: "Embed a Google Form for applications, surveys, or contact",
    icon: <DynamicForm />,
    color: "#1565c0",
    defaults: {
      embed_url: "",
      title: "",
      height: 600,
    },
  },
  {
    type: "gallery",
    label: "Image Gallery",
    description: "Grid of photos with optional captions",
    icon: <PhotoLibrary />,
    color: "#ad1457",
    defaults: {
      title: "",
      columns: 3,
      bg_color: "#ffffff",
      items: [
        { image: "", caption: "" },
        { image: "", caption: "" },
        { image: "", caption: "" },
      ],
    },
  },
  {
    type: "slider",
    label: "Image Slider",
    description: "Auto-cycling slideshow of images with captions",
    icon: <Slideshow />,
    color: "#00838f",
    defaults: {
      title: "",
      autoplay: true,
      interval: 4000,
      show_captions: true,
      items: [
        { image: "", caption: "Slide 1" },
        { image: "", caption: "Slide 2" },
      ],
    },
  },
];

// ─── Page template presets ────────────────────────────────────────────────────
export const PAGE_TEMPLATES = [
  {
    id: "school-home",
    label: "School Home Page",
    description: "Hero banner → Stats → About → Features → CTA",
    thumbnail: "🏫",
    blocks: [
      {
        type: "hero",
        title: "Welcome to Our School",
        subtitle: "Inspiring minds, building futures since 1985",
        bg_color: "#1a237e",
        text_color: "#ffffff",
        bg_image: "",
        cta_label: "Explore",
        cta_url: "/blog",
        min_height: 520,
        overlay_opacity: 0.4,
      },
      {
        type: "banner",
        text: "🎉 Applications for the 2025/2026 academic year are now open!",
        bg_color: "#e91e63",
        text_color: "#ffffff",
        cta_label: "Apply Now",
        cta_url: "/blog",
      },
      {
        type: "stats",
        title: "Our School in Numbers",
        bg_color: "#f5f7ff",
        text_color: "#1a237e",
        label_color: "#555",
        items: [
          { value: "2,500+", label: "Students" },
          { value: "150+", label: "Staff" },
          { value: "40+", label: "Years" },
          { value: "98%", label: "Pass rate" },
        ],
      },
      {
        type: "image_text",
        title: "About Our School",
        text: "For over 40 years we have been committed to academic excellence and character development. Our dedicated staff and modern facilities create the perfect learning environment.",
        image: "",
        image_position: "left",
        bg_color: "#ffffff",
        text_color: "#1a1a1a",
        cta_label: "Read More",
        cta_url: "/blog",
      },
      {
        type: "features",
        title: "Why Choose Us",
        bg_color: "#f8f9fa",
        text_color: "#1a237e",
        columns: 3,
        items: [
          { emoji: "🎓", title: "Academic Excellence", description: "Top-ranked programmes and qualified teachers." },
          { emoji: "🏫", title: "Modern Facilities", description: "State-of-the-art classrooms, labs and library." },
          { emoji: "⚽", title: "Sports & Activities", description: "Diverse extracurricular programmes for every student." },
          { emoji: "🌍", title: "Global Outlook", description: "Preparing students for success in a global world." },
          { emoji: "🤝", title: "Community", description: "A warm, inclusive school community." },
          { emoji: "📈", title: "Career Ready", description: "Guidance and counselling from Year 1 to graduation." },
        ],
      },
      {
        type: "cta",
        title: "Ready to Join Our Family?",
        text: "Apply now for the upcoming academic year. Spaces are limited.",
        bg_color: "#1565c0",
        text_color: "#ffffff",
        cta_label: "Start Application",
        cta_url: "/blog",
      },
    ],
    content: "",
  },
  {
    id: "about-page",
    label: "About Page",
    description: "Hero → Image+Text → Stats → Features",
    thumbnail: "📖",
    blocks: [
      {
        type: "hero",
        title: "About Us",
        subtitle: "Learn more about our history, mission, and values",
        bg_color: "#2e7d32",
        text_color: "#ffffff",
        bg_image: "",
        cta_label: "",
        cta_url: "",
        min_height: 340,
        overlay_opacity: 0.4,
      },
      {
        type: "image_text",
        title: "Our Story",
        text: "Founded in 1985, our school has grown from a small community school to one of the most respected institutions in the region, with a proud tradition of academic and extracurricular excellence.",
        image: "",
        image_position: "left",
        bg_color: "#ffffff",
        text_color: "#1a1a1a",
        cta_label: "",
        cta_url: "",
      },
      {
        type: "stats",
        title: "",
        bg_color: "#f5f7ff",
        text_color: "#2e7d32",
        label_color: "#555",
        items: [
          { value: "1985", label: "Founded" },
          { value: "2,500+", label: "Students" },
          { value: "150+", label: "Staff" },
          { value: "40+", label: "Years of excellence" },
        ],
      },
      {
        type: "image_text",
        title: "Our Mission",
        text: "We are committed to nurturing the intellectual, social, and emotional growth of every student, providing a safe and stimulating environment where every learner can thrive.",
        image: "",
        image_position: "right",
        bg_color: "#ffffff",
        text_color: "#1a1a1a",
        cta_label: "",
        cta_url: "",
      },
    ],
    content: "",
  },
  {
    id: "contact-page",
    label: "Contact Page",
    description: "Banner → CTA with directions",
    thumbnail: "📬",
    blocks: [
      {
        type: "hero",
        title: "Contact Us",
        subtitle: "We'd love to hear from you",
        bg_color: "#37474f",
        text_color: "#ffffff",
        bg_image: "",
        cta_label: "",
        cta_url: "",
        min_height: 300,
        overlay_opacity: 0.4,
      },
      {
        type: "features",
        title: "Get in Touch",
        bg_color: "#f8f9fa",
        text_color: "#1a237e",
        columns: 3,
        items: [
          { emoji: "📍", title: "Visit Us", description: "123 School Road, Your City" },
          { emoji: "📞", title: "Call Us", description: "+1 (555) 123-4567" },
          { emoji: "✉️", title: "Email Us", description: "info@school.edu" },
        ],
      },
    ],
    content: "",
  },
  {
    id: "admissions-page",
    label: "Admissions Page",
    description: "Hero → Stats → How to Apply → Requirements → CTA",
    thumbnail: "📝",
    blocks: [
      {
        type: "hero",
        title: "Admissions",
        subtitle: "Join our school community — applications are now open",
        bg_color: "#4527a0",
        text_color: "#ffffff",
        bg_image: "",
        cta_label: "Apply Now",
        cta_url: "/blog",
        min_height: 380,
        overlay_opacity: 0.45,
      },
      {
        type: "stats",
        title: "2025/2026 Admissions at a Glance",
        bg_color: "#f5f7ff",
        text_color: "#4527a0",
        label_color: "#555",
        items: [
          { value: "250", label: "Places available" },
          { value: "Jan 31", label: "Application deadline" },
          { value: "Mar 15", label: "Offers sent" },
          { value: "6 weeks", label: "Orientation period" },
        ],
      },
      {
        type: "image_text",
        title: "How to Apply",
        text: "1. Complete the online application form.\n2. Submit required documents (birth certificate, last report card, passport photo).\n3. Attend the entrance assessment if shortlisted.\n4. Receive your offer letter and confirm your place.",
        image: "",
        image_position: "right",
        bg_color: "#ffffff",
        text_color: "#1a1a1a",
        cta_label: "Start Application",
        cta_url: "/blog",
      },
      {
        type: "features",
        title: "Required Documents",
        bg_color: "#f8f9fa",
        text_color: "#4527a0",
        columns: 3,
        items: [
          { emoji: "📄", title: "Birth Certificate", description: "Original or certified copy." },
          { emoji: "📊", title: "Previous Report Card", description: "Last two semesters." },
          { emoji: "📸", title: "Passport Photo", description: "Two recent passport-size photos." },
          { emoji: "💉", title: "Vaccination Records", description: "Up-to-date immunisation history." },
          { emoji: "🏠", title: "Proof of Address", description: "Utility bill or tenancy agreement." },
          { emoji: "📋", title: "Completed Form", description: "Signed parent / guardian declaration." },
        ],
      },
      {
        type: "cta",
        title: "Ready to Apply?",
        text: "Complete your application online today. Our admissions team is here to help.",
        bg_color: "#4527a0",
        text_color: "#ffffff",
        cta_label: "Apply Online",
        cta_url: "/blog",
      },
    ],
    content: "",
  },
  {
    id: "staff-page",
    label: "Staff / Faculty Page",
    description: "Hero → Features grid of staff cards",
    thumbnail: "👨‍🏫",
    blocks: [
      {
        type: "hero",
        title: "Meet Our Team",
        subtitle: "Dedicated professionals committed to your child's success",
        bg_color: "#00695c",
        text_color: "#ffffff",
        bg_image: "",
        cta_label: "",
        cta_url: "",
        min_height: 340,
        overlay_opacity: 0.4,
      },
      {
        type: "features",
        title: "Leadership",
        bg_color: "#ffffff",
        text_color: "#00695c",
        columns: 3,
        items: [
          { emoji: "👩‍💼", title: "Mrs. A. Johnson", description: "Principal" },
          { emoji: "👨‍💼", title: "Mr. B. Williams", description: "Vice Principal (Academics)" },
          { emoji: "👩‍💼", title: "Mrs. C. Davis", description: "Vice Principal (Student Affairs)" },
        ],
      },
      {
        type: "features",
        title: "Teaching Staff",
        bg_color: "#f8f9fa",
        text_color: "#00695c",
        columns: 4,
        items: [
          { emoji: "📐", title: "Mr. D. Brown", description: "Mathematics" },
          { emoji: "🔬", title: "Ms. E. Wilson", description: "Sciences" },
          { emoji: "📚", title: "Mrs. F. Moore", description: "English Language" },
          { emoji: "🌍", title: "Mr. G. Taylor", description: "Social Studies" },
          { emoji: "🎨", title: "Ms. H. Anderson", description: "Visual Arts" },
          { emoji: "🎵", title: "Mr. I. Thomas", description: "Music" },
          { emoji: "⚽", title: "Mr. J. Jackson", description: "Physical Education" },
          { emoji: "💻", title: "Ms. K. White", description: "ICT" },
        ],
      },
    ],
    content: "",
  },
  {
    id: "gallery-page",
    label: "Gallery / Media Page",
    description: "Hero → YouTube video → Image galleries with captions",
    thumbnail: "🖼️",
    blocks: [
      {
        type: "hero",
        title: "Gallery",
        subtitle: "A glimpse into life at our school",
        bg_color: "#1565c0",
        text_color: "#ffffff",
        bg_image: "",
        cta_label: "",
        cta_url: "",
        min_height: 320,
        overlay_opacity: 0.4,
      },
      {
        type: "youtube",
        url: "",
        title: "School Tour — watch our virtual campus walk",
        aspect_ratio: "16/9",
        bg_color: "#000000",
      },
      {
        type: "image_text",
        title: "Our Campus",
        text: "Explore our modern facilities including science labs, a library, sports fields, and creative arts studios.",
        image: "",
        image_position: "left",
        bg_color: "#ffffff",
        text_color: "#1a1a1a",
        cta_label: "",
        cta_url: "",
      },
      {
        type: "image_text",
        title: "Student Life",
        text: "From sports days to drama productions and science fairs — there's always something happening at our school.",
        image: "",
        image_position: "right",
        bg_color: "#f8f9fa",
        text_color: "#1a1a1a",
        cta_label: "",
        cta_url: "",
      },
    ],
    content: "",
  },
];

// ─── Individual block field editors ──────────────────────────────────────────
function ColorField({ label, value, onChange }) {
  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
      <Box
        component="input"
        type="color"
        value={value || "#ffffff"}
        onChange={(e) => onChange(e.target.value)}
        sx={{ width: 36, height: 36, border: "1px solid #ccc", borderRadius: 1, cursor: "pointer", p: 0.3 }}
      />
      <TextField
        label={label}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{ flex: 1 }}
      />
    </Box>
  );
}

function ImageUploadField({ label, value, onChange }) {
  const [uploadFile, { isLoading }] = useUploadFileMutation();

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await uploadFile(fd).unwrap();
      onChange(res.url);
    } catch {}
    e.target.value = "";
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <TextField
        label={label}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        fullWidth size="small" placeholder="Paste URL or upload"
        sx={{ mb: 0.5 }}
      />
      <Button component="label" variant="outlined" size="small" disabled={isLoading}>
        {isLoading ? "Uploading…" : "Upload Image"}
        <input type="file" accept="image/*" hidden onChange={handleFile} />
      </Button>
      {value && (
        <Box component="img" src={value} sx={{ mt: 1, width: "100%", maxHeight: 120, objectFit: "cover", borderRadius: 1 }} />
      )}
    </Box>
  );
}

function VideoUploadField({ label, value, onChange }) {
  const [uploadFile, { isLoading }] = useUploadFileMutation();
  const MAX_MB = 20;

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      alert(`Video must be under ${MAX_MB} MB. Selected file is ${(file.size / 1024 / 1024).toFixed(1)} MB.`);
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await uploadFile(fd).unwrap();
      onChange(res.url);
    } catch {}
    e.target.value = "";
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <TextField
        label={label}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        fullWidth size="small" placeholder="Paste URL or upload (max 20 MB)"
        sx={{ mb: 0.5 }}
      />
      <Button component="label" variant="outlined" size="small" disabled={isLoading}>
        {isLoading ? "Uploading…" : "Upload Video (max 20 MB)"}
        <input type="file" accept="video/*" hidden onChange={handleFile} />
      </Button>
      {value && (
        <Box component="video" src={value} controls sx={{ mt: 1, width: "100%", maxHeight: 160, borderRadius: 1 }} />
      )}
    </Box>
  );
}

function BlockEditForm({ blockType, data, onChange }) {
  const f = (field) => (val) => onChange({ ...data, [field]: val });
  const t = (field) => (e) => onChange({ ...data, [field]: e.target.value });

  // Stats items helpers
  function updateStatItem(i, key, val) {
    const items = [...(data.items || [])];
    items[i] = { ...items[i], [key]: val };
    onChange({ ...data, items });
  }
  function addStatItem() {
    onChange({ ...data, items: [...(data.items || []), { value: "", label: "" }] });
  }
  function removeStatItem(i) {
    onChange({ ...data, items: (data.items || []).filter((_, idx) => idx !== i) });
  }

  // Features items helpers
  function updateFeatureItem(i, key, val) {
    const items = [...(data.items || [])];
    items[i] = { ...items[i], [key]: val };
    onChange({ ...data, items });
  }
  function addFeatureItem() {
    onChange({ ...data, items: [...(data.items || []), { emoji: "⭐", title: "", description: "" }] });
  }
  function removeFeatureItem(i) {
    onChange({ ...data, items: (data.items || []).filter((_, idx) => idx !== i) });
  }

  switch (blockType) {
    case "hero":
      return (
        <Box>
          <TextField label="Title" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="Subtitle" value={data.subtitle || ""} onChange={t("subtitle")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <ImageUploadField label="Background Image URL" value={data.bg_image} onChange={f("bg_image")} />
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
          <ColorField label="Text colour" value={data.text_color} onChange={f("text_color")} />
          <TextField label="Overlay opacity (0–1)" type="number" inputProps={{ min: 0, max: 1, step: 0.05 }} value={data.overlay_opacity ?? 0.45} onChange={t("overlay_opacity")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="Min height (px)" type="number" value={data.min_height || 480} onChange={t("min_height")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <Divider sx={{ my: 1.5 }} />
          <TextField label="CTA Button Label" value={data.cta_label || ""} onChange={t("cta_label")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="CTA Button URL" value={data.cta_url || ""} onChange={t("cta_url")} fullWidth size="small" />
        </Box>
      );
    case "banner":
      return (
        <Box>
          <TextField label="Text" value={data.text || ""} onChange={t("text")} fullWidth size="small" multiline rows={2} sx={{ mb: 1.5 }} />
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
          <ColorField label="Text colour" value={data.text_color} onChange={f("text_color")} />
          <Divider sx={{ my: 1.5 }} />
          <TextField label="Button Label" value={data.cta_label || ""} onChange={t("cta_label")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="Button URL" value={data.cta_url || ""} onChange={t("cta_url")} fullWidth size="small" />
        </Box>
      );
    case "stats":
      return (
        <Box>
          <TextField label="Section title (optional)" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
          <ColorField label="Number colour" value={data.text_color} onChange={f("text_color")} />
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" mb={1}>Stats</Typography>
          {(data.items || []).map((item, i) => (
            <Box key={i} display="flex" gap={1} alignItems="center" mb={1}>
              <TextField size="small" label="Value" value={item.value} onChange={(e) => updateStatItem(i, "value", e.target.value)} sx={{ flex: 1 }} />
              <TextField size="small" label="Label" value={item.label} onChange={(e) => updateStatItem(i, "label", e.target.value)} sx={{ flex: 1 }} />
              <IconButton size="small" onClick={() => removeStatItem(i)}><Delete fontSize="small" /></IconButton>
            </Box>
          ))}
          <Button startIcon={<Add />} size="small" onClick={addStatItem}>Add stat</Button>
        </Box>
      );
    case "cta":
      return (
        <Box>
          <TextField label="Title" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="Body text" value={data.text || ""} onChange={t("text")} fullWidth size="small" multiline rows={2} sx={{ mb: 1.5 }} />
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
          <ColorField label="Text colour" value={data.text_color} onChange={f("text_color")} />
          <Divider sx={{ my: 1.5 }} />
          <TextField label="Button Label" value={data.cta_label || ""} onChange={t("cta_label")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="Button URL" value={data.cta_url || ""} onChange={t("cta_url")} fullWidth size="small" />
        </Box>
      );
    case "image_text":
      return (
        <Box>
          <TextField label="Title" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="Body text" value={data.text || ""} onChange={t("text")} fullWidth size="small" multiline rows={3} sx={{ mb: 1.5 }} />
          <ImageUploadField label="Image URL" value={data.image} onChange={f("image")} />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Image position</InputLabel>
            <Select value={data.image_position || "left"} label="Image position" onChange={t("image_position")}>
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </Select>
          </FormControl>
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
          <ColorField label="Text colour" value={data.text_color} onChange={f("text_color")} />
          <Divider sx={{ my: 1.5 }} />
          <TextField label="Button Label (optional)" value={data.cta_label || ""} onChange={t("cta_label")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="Button URL" value={data.cta_url || ""} onChange={t("cta_url")} fullWidth size="small" />
        </Box>
      );
    case "features":
      return (
        <Box>
          <TextField label="Section title (optional)" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
          <ColorField label="Title colour" value={data.text_color} onChange={f("text_color")} />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Columns</InputLabel>
            <Select value={data.columns || 3} label="Columns" onChange={(e) => onChange({ ...data, columns: e.target.value })}>
              {[2, 3, 4].map((n) => <MenuItem key={n} value={n}>{n} columns</MenuItem>)}
            </Select>
          </FormControl>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" mb={1}>Cards</Typography>
          {(data.items || []).map((item, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" fontWeight={600}>Card {i + 1}</Typography>
                <IconButton size="small" onClick={() => removeFeatureItem(i)}><Delete fontSize="small" /></IconButton>
              </Box>
              <TextField size="small" label="Emoji" value={item.emoji || ""} onChange={(e) => updateFeatureItem(i, "emoji", e.target.value)} sx={{ mr: 1, mb: 1, width: 80 }} />
              <TextField size="small" label="Title" value={item.title || ""} onChange={(e) => updateFeatureItem(i, "title", e.target.value)} fullWidth sx={{ mb: 1 }} />
              <TextField size="small" label="Description" value={item.description || ""} onChange={(e) => updateFeatureItem(i, "description", e.target.value)} fullWidth multiline rows={2} />
            </Paper>
          ))}
          <Button startIcon={<Add />} size="small" onClick={addFeatureItem}>Add card</Button>
        </Box>
      );
    case "spacer":
      return (
        <Box>
          <TextField label="Height (px)" type="number" value={data.height || 60} onChange={t("height")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
        </Box>
      );
    case "richtext":
      return (
        <Box>
          <CustomTipTapEditor
            key={data.id}
            value={data.html || ""}
            onChange={(html) => onChange({ ...data, html })}
            minHeight="300px"
          />
        </Box>
      );
    case "youtube":
      return (
        <Box>
          <TextField
            label="YouTube URL or Video ID"
            value={data.url || ""}
            onChange={t("url")}
            fullWidth size="small" sx={{ mb: 1.5 }}
            placeholder="https://www.youtube.com/watch?v=... or video ID"
            helperText="Paste the full YouTube link or just the 11-character video ID"
          />
          <TextField label="Caption (optional)" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Aspect ratio</InputLabel>
            <Select value={data.aspect_ratio || "16/9"} label="Aspect ratio" onChange={t("aspect_ratio")}>
              <MenuItem value="16/9">16:9 (widescreen)</MenuItem>
              <MenuItem value="4/3">4:3 (classic)</MenuItem>
              <MenuItem value="1/1">1:1 (square)</MenuItem>
            </Select>
          </FormControl>
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
        </Box>
      );
    case "video":
      return (
        <Box>
          <VideoUploadField label="Video URL" value={data.url} onChange={f("url")} />
          <TextField label="Caption (optional)" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <ImageUploadField label="Poster / Thumbnail URL" value={data.poster} onChange={f("poster")} />
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
        </Box>
      );
    case "map":
      return (
        <Box>
          <TextField
            label="Google Maps Embed URL"
            value={data.embed_url || ""}
            onChange={t("embed_url")}
            fullWidth size="small" sx={{ mb: 1.5 }}
            placeholder="https://www.google.com/maps/embed?pb=..."
            helperText='In Google Maps click Share → Embed a map → copy the src="..." URL'
            multiline rows={3}
          />
          <TextField label="Caption (optional)" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="Map height (px)" type="number" value={data.height || 400} onChange={t("height")} fullWidth size="small" sx={{ mb: 1.5 }} />
        </Box>
      );
    case "google_form":
      return (
        <Box>
          <TextField
            label="Google Form Embed URL"
            value={data.embed_url || ""}
            onChange={t("embed_url")}
            fullWidth size="small" sx={{ mb: 1.5 }}
            placeholder="https://docs.google.com/forms/d/.../viewform?embedded=true"
            helperText='In Google Forms click Send → Embed (</>) → copy the src="..." URL'
            multiline rows={3}
          />
          <TextField label="Section title (optional)" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <TextField label="Form height (px)" type="number" value={data.height || 600} onChange={t("height")} fullWidth size="small" sx={{ mb: 1.5 }} />
        </Box>
      );
    case "gallery":
      return (
        <Box>
          <TextField label="Section title (optional)" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Columns</InputLabel>
            <Select value={data.columns || 3} label="Columns" onChange={(e) => onChange({ ...data, columns: e.target.value })}>
              {[2, 3, 4].map((n) => <MenuItem key={n} value={n}>{n} columns</MenuItem>)}
            </Select>
          </FormControl>
          <ColorField label="Background colour" value={data.bg_color} onChange={f("bg_color")} />
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" mb={1}>Images</Typography>
          {(data.items || []).map((item, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" fontWeight={600}>Image {i + 1}</Typography>
                <IconButton size="small" onClick={() => onChange({ ...data, items: data.items.filter((_, idx) => idx !== i) })}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
              <ImageUploadField
                label="Image"
                value={item.image}
                onChange={(url) => {
                  const items = [...data.items];
                  items[i] = { ...items[i], image: url };
                  onChange({ ...data, items });
                }}
              />
              <TextField
                size="small" label="Caption (optional)" fullWidth
                value={item.caption || ""}
                onChange={(e) => {
                  const items = [...data.items];
                  items[i] = { ...items[i], caption: e.target.value };
                  onChange({ ...data, items });
                }}
              />
            </Paper>
          ))}
          <Button startIcon={<Add />} size="small" onClick={() => onChange({ ...data, items: [...(data.items || []), { image: "", caption: "" }] })}>
            Add image
          </Button>
        </Box>
      );
    case "slider":
      return (
        <Box>
          <TextField label="Section title (optional)" value={data.title || ""} onChange={t("title")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Autoplay</InputLabel>
            <Select value={data.autoplay ?? true} label="Autoplay" onChange={(e) => onChange({ ...data, autoplay: e.target.value })}>
              <MenuItem value={true}>On</MenuItem>
              <MenuItem value={false}>Off</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Slide interval (ms)" type="number" value={data.interval || 4000} onChange={t("interval")} fullWidth size="small" sx={{ mb: 1.5 }} />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Show captions</InputLabel>
            <Select value={data.show_captions ?? true} label="Show captions" onChange={(e) => onChange({ ...data, show_captions: e.target.value })}>
              <MenuItem value={true}>Yes</MenuItem>
              <MenuItem value={false}>No</MenuItem>
            </Select>
          </FormControl>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" mb={1}>Slides</Typography>
          {(data.items || []).map((item, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" fontWeight={600}>Slide {i + 1}</Typography>
                <IconButton size="small" onClick={() => onChange({ ...data, items: data.items.filter((_, idx) => idx !== i) })}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
              <ImageUploadField
                label="Image"
                value={item.image}
                onChange={(url) => {
                  const items = [...data.items];
                  items[i] = { ...items[i], image: url };
                  onChange({ ...data, items });
                }}
              />
              <TextField
                size="small" label="Caption" fullWidth
                value={item.caption || ""}
                onChange={(e) => {
                  const items = [...data.items];
                  items[i] = { ...items[i], caption: e.target.value };
                  onChange({ ...data, items });
                }}
              />
            </Paper>
          ))}
          <Button startIcon={<Add />} size="small" onClick={() => onChange({ ...data, items: [...(data.items || []), { image: "", caption: "" }] })}>
            Add slide
          </Button>
        </Box>
      );
    default:
      return null;
  }
}

// ─── Main builder ─────────────────────────────────────────────────────────────
export default function PageBlocksBuilder({ blocks = [], onChange, onLoadTemplate }) {
  const [addOpen, setAddOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [editIdx, setEditIdx] = useState(null); // index of block being edited
  const [editData, setEditData] = useState(null);

  function generateId() {
    return Math.random().toString(36).slice(2, 9);
  }

  function addBlock(blockTypeDef) {
    const newBlock = { ...blockTypeDef.defaults, type: blockTypeDef.type, id: generateId() };
    onChange([...blocks, newBlock]);
    setAddOpen(false);
  }

  function removeBlock(idx) {
    onChange(blocks.filter((_, i) => i !== idx));
  }

  function moveBlock(idx, dir) {
    const arr = [...blocks];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    onChange(arr);
  }

  function openEdit(idx) {
    setEditIdx(idx);
    setEditData({ ...blocks[idx] });
  }

  function saveEdit() {
    const arr = [...blocks];
    arr[editIdx] = editData;
    onChange(arr);
    setEditIdx(null);
    setEditData(null);
  }

  function applyTemplate(tpl) {
    const blocks = tpl.blocks.map((b) => ({ ...b, id: generateId() }));
    onChange(blocks);
    if (onLoadTemplate) onLoadTemplate(tpl.content || "");
    setTemplateOpen(false);
  }

  const blockTypeMeta = (type) => BLOCK_TYPES.find((b) => b.type === type);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle1" fontWeight={700}>Page Blocks</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setTemplateOpen(true)}
          >
            Load Template
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => setAddOpen(true)}
          >
            Add Block
          </Button>
        </Stack>
      </Box>

      {/* Block list */}
      {blocks.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4, textAlign: "center", borderStyle: "dashed",
            color: "text.secondary", borderRadius: 2,
          }}
        >
          <Typography variant="body2">
            No blocks yet. Use "Load Template" to start from a preset or "Add Block" to build from scratch.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {blocks.map((block, idx) => {
            const meta = blockTypeMeta(block.type);
            return (
              <Paper
                key={block.id || idx}
                variant="outlined"
                sx={{ px: 2, py: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  sx={{
                    width: 10,
                    alignSelf: "stretch",
                    bgcolor: meta?.color || "#ccc",
                    borderRadius: 1,
                    mr: 1,
                    flexShrink: 0,
                  }}
                />
                <Box flex={1} minWidth={0}>
                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                    {meta?.label || block.type}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {block.title || block.text || ""}
                  </Typography>
                </Box>
                <Chip label={block.type} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                <Box display="flex" flexShrink={0}>
                  <Tooltip title="Move up"><span><IconButton size="small" disabled={idx === 0} onClick={() => moveBlock(idx, -1)}><ArrowUpward fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Move down"><span><IconButton size="small" disabled={idx === blocks.length - 1} onClick={() => moveBlock(idx, 1)}><ArrowDownward fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(idx)}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => removeBlock(idx)}><Delete fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* ── Add block dialog ── */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Choose a Block Type</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} pt={1}>
            {BLOCK_TYPES.map((bt) => (
              <Grid item xs={12} sm={6} key={bt.type}>
                <Card
                  variant="outlined"
                  sx={{ cursor: "pointer", "&:hover": { boxShadow: 3 } }}
                  onClick={() => addBlock(bt)}
                >
                  <CardActionArea sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box sx={{ color: bt.color, display: "flex" }}>{bt.icon}</Box>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>{bt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{bt.description}</Typography>
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit block dialog ── */}
      <Dialog open={editIdx !== null} onClose={() => setEditIdx(null)} maxWidth={["richtext", "youtube", "video", "map", "google_form", "gallery", "slider"].includes(editData?.type) ? "md" : "sm"} fullWidth>
        <DialogTitle>
          Edit — {blockTypeMeta(editData?.type)?.label || editData?.type}
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          {editData && (
            <BlockEditForm
              blockType={editData.type}
              data={editData}
              onChange={setEditData}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditIdx(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit}>Save Block</Button>
        </DialogActions>
      </Dialog>

      {/* ── Templates dialog ── */}
      <Dialog open={templateOpen} onClose={() => setTemplateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Load a Template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Loading a template will replace all current blocks on this page.
          </Typography>
          <Stack spacing={1.5} pt={0.5}>
            {PAGE_TEMPLATES.map((tpl) => (
              <Card key={tpl.id} variant="outlined" sx={{ cursor: "pointer", "&:hover": { boxShadow: 3 } }}>
                <CardActionArea onClick={() => applyTemplate(tpl)} sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h4">{tpl.thumbnail}</Typography>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>{tpl.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{tpl.description}</Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
